import { card } from "@/repositories/cards";
import { formatArrayAsString, formatPhoneNumber } from "./utils";
import { auth } from "@/services/auth";
import {
  ActivityFeed,
  Card,
  CardContents,
  CardFieldKey,
  CardFieldKeys,
  CreateCardDto,
  Employee,
  IconNames,
  Integration,
  Shop,
  PatchCardDto,
  TagsDocument,
} from "@/types/v2";
import shortUUID from "short-uuid";
import { Employee as EmployeeV1 } from "@/types/v1";
import { httpsCallables } from "@/repositories/httpsCallables";
import {
  DEFAULT_CARD_CONTENTS,
  DEFAULT_COLORS,
  DEFAULT_SHOW_LABELS_AS_TAGS,
} from "./const";
import { TagsRepository } from "@/repositories/tags";
import { firestoreToDateTime } from "./utils";
import { createSubdomainUrl } from "@/lib/createSubdomainUrl";

/**
 * Removes a card by updating its status to "manuallyClosed" and then
 * moves the card using an HTTPS callable function.
 * @param {string} shopId - The unique identifier for the shop.
 * @param {string} cardId - The unique identifier for the card.
 * @returns {Promise<any>} A promise that resolves with the result of the HTTPS callable moveCard function.
 */
export async function removeCard(shopId: string, cardId: string) {
  await card.update(shopId, cardId, {
    status: "manuallyClosed",
  });
  return await httpsCallables.moveCard({ shopId, cardId });
}

/**
 * Marks a card as complete by updating its status to "completed" and setting
 * the completion date, then moves the card using an HTTPS callable function.
 * @param {string} shopId - The unique identifier for the shop.
 * @param {string} cardId - The unique identifier for the card.
 * @param {Date | null} [completedDate] - Optional completion date. If not provided, uses current date/time.
 * @returns {Promise<any>} A promise that resolves with the result of the HTTPS callable moveCard function.
 */
export async function markCardAsComplete(
  shopId: string,
  cardId: string,
  completedDate?: Date | null
) {
  await card.update(shopId, cardId, {
    status: "completed",
    completedDate: completedDate ?? new Date(),
  });
  return await httpsCallables.moveCard({ shopId, cardId });
}

/**
 * Updates a card with form data from CardFormDialog.
 * Handles tag creation, card updates, and marking as complete if needed.
 * @param {string} shopId - The unique identifier for the shop.
 * @param {string} cardId - The unique identifier for the card.
 * @param {PatchCardDto} values - The form values to update the card with.
 * @param {Card} cardData - The current card data.
 * @param {TagsDocument | undefined} tags - The current tags document.
 * @returns {Promise<void>} A promise that resolves when the card has been updated.
 */
export async function updateCardWithForm(
  shopId: string,
  cardId: string,
  values: PatchCardDto,
  cardData: Card,
  tags: TagsDocument | undefined
) {
  // Add new tags if provided
  if (values.tags && values.tags.length > 0) {
    await TagsRepository.addTag(shopId, values.tags, tags);
  }

  // Update the card with the new values
  await card.update(shopId, cardId, {
    ...values,
    _isSoftMigrated: cardData._isSoftMigrated,
  });

  // If completedDate is set and the card wasn't already completed, mark it as complete
  if (values.completedDate && !cardData.completedDate) {
    const completedDate = firestoreToDateTime(values.completedDate);
    await markCardAsComplete(shopId, cardId, completedDate);
  }
}

/**
 * Formats the given labels. If a single string is provided, it returns the string.
 * If an array of strings is provided, it formats the array as a single concatenated string.
 * @param {string|string[]} labels - The labels to be formatted. This can be a single string or an array of strings.
 * @returns {string} The formatted string.
 */
export function formatLabels(labels: string | string[]) {
  if (typeof labels === "string") {
    return labels;
  }
  return formatArrayAsString(labels);
}

/**
 * Whether a card's DMS-synced label should render as an extra tag-styled
 * pill on the board card. Purely manual — no auto-suppression based on the
 * card's container. See Card.labelAsTagEnabled and
 * ShopConfig.showLabelsAsTags for the two inputs this composes.
 */
export function shouldShowLabelAsTag(
  cardData: Pick<Card, "label" | "labelAsTagEnabled">,
  shop?: Pick<Shop, "shopConfig">
): boolean {
  if (!cardData.label) return false;
  const shopDefault =
    shop?.shopConfig?.showLabelsAsTags ?? DEFAULT_SHOW_LABELS_AS_TAGS;
  return cardData.labelAsTagEnabled ?? shopDefault;
}

/**
 * Builds the Firestore patch for an "Edit Card Tags" submission, shared by
 * every call site (useCardMenuActions, RoDetailsSummary). The label-as-tag
 * override is a separate, dedicated card-menu action (see
 * useCardMenuActions.handleToggleLabelAsTag) and is never touched here.
 */
export function buildTagEditPatch(
  cardData: Pick<Card, "_isSoftMigrated">,
  values: { tags: string[] }
): PatchCardDto {
  return {
    tags: values.tags,
    _isSoftMigrated: cardData._isSoftMigrated,
  };
}

/**
 * Adds a new note to a card identified by `shopId` and `cardId`. If `cardData` is not provided,
 * it fetches the card data first. The note is appended to the card's existing notes.
 * @param {string} shopId - The unique identifier of the shop.
 * @param {string} note - The content of the note to be added.
 * @param {string} cardId - The unique identifier of the card to which the note will be added.
 * @param {Card} [cardData] - Optional. The card data object. If not provided, it will be fetched.
 * @returns {Promise<void>} - A promise that resolves when the note has been added.
 */
export async function addNote(
  shopId: string,
  note: string,
  cardId: string,
  cardData?: Card
) {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  if (!cardData) {
    cardData = await card.get(shopId, cardId);
  }
  if (!cardData) return;
  const notes = cardData.notes ?? [];
  notes.push({
    id: shortUUID().generate().toString(),
    text: note,
    createdAt: new Date(),
    createdBy: uid,
    updatedAt: new Date(),
    updatedBy: uid,
  });
  return card.update(shopId, cardId, { notes: notes });
}

/**
 * Deletes a note from a card in a shop.
 * @param {string} shopId - The unique identifier of the shop.
 * @param {string} noteId - The unique identifier of the note to be deleted.
 * @param {string} cardId - The unique identifier of the card containing the note.
 * @param {Card} [cardData] - Optional card data, if already available.
 * @returns {Promise<void>} A promise indicating the completion of the note deletion.
 */
export async function deleteNote(
  shopId: string,
  noteId: string,
  cardId: string,
  cardData?: Card
) {
  if (!cardData) {
    cardData = await card.get(shopId, cardId);
  }
  if (!cardData) return;
  const notes = cardData.notes ?? [];
  const newNotes = notes.filter((note) => note.id !== noteId);
  return card.update(shopId, cardId, {
    notes: newNotes,
    updatedBy: auth.currentUser?.uid ?? cardData.updatedBy ?? "UNKNOWN",
  });
}

/**
 * Updates an existing note associated with a specific card in a specific shop.
 * @param {string} shopId - The unique identifier of the shop.
 * @param {string} noteId - The unique identifier of the note to be updated.
 * @param {string} note - The new text/content of the note.
 * @param {string} cardId - The unique identifier of the card associated with the note.
 * @param {Card} [cardData] - Optional card data to be used for the update. If not provided, it will be fetched.
 * @returns {Promise<void>} A promise that resolves when the note is successfully updated.
 */
export async function updateNote(
  shopId: string,
  noteId: string,
  note: string,
  cardId: string,
  cardData?: Card
) {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  if (!cardData) {
    cardData = await card.get(shopId, cardId);
  }
  if (!cardData) return;
  const notes = cardData.notes ?? [];
  const updatedNotes = notes.map((n) =>
    n.id === noteId
      ? {
          ...n,
          text: note,
          updatedAt: new Date(),
          updatedBy: uid,
        }
      : n
  );
  return card.update(shopId, cardId, { notes: updatedNotes });
}

/**
 * Formats technician names based on the provided technician IDs.
 * @param {string | number | string[]} technicianIds - The ID or IDs of the technicians whose names need to be formatted.
 * @param {Array<Employee | EmployeeV1>} employees - The list of employee objects where each object can be of type Employee or EmployeeV1.
 * @returns {string} - A formatted string with the technician names.
 */
export function formatTechnicianNames(
  technicianIds: string | number | string[],
  employees: (Employee | EmployeeV1)[]
) {
  const linkedTechnicianType = typeof technicianIds;
  if (linkedTechnicianType === "string") {
    const employee = employees?.find((emp) => `${emp.id}` === technicianIds);
    if (!employee) return "no valid technician";
    return `${employee?.firstName} ${employee?.lastName}`;
  }
  if (linkedTechnicianType === "number") {
    const employee = employees?.find((emp) => emp.id === technicianIds);
    return `${employee?.firstName} ${employee?.lastName}`;
  }

  const filteredEmployees = employees?.filter((emp) =>
    (technicianIds as string[]).includes(`${emp.id}`)
  );

  if (!filteredEmployees || filteredEmployees.length === 0) {
    return "Unknown";
  }

  return formatArrayAsString(
    filteredEmployees.map((emp) => `${emp?.firstName} ${emp?.lastName}`)
  );
}

/**
 * Cleanses the input by converting `undefined` or `null` values to an empty string.
 * For non-null/undefined input, it returns the input converted to a string.
 * @param {string | number | null | undefined} input - The input value to cleanse.
 * @returns {string} - The cleansed string.
 */
export function cleanseUndefined(input: string | number | null | undefined) {
  if (!input) return "";
  return `${input}`;
}

/**
 * Retrieves the label for a specified card field section from the shop's card configuration.
 * @param {Shop} shop - The shop object containing card configuration.
 * @param {CardFieldKeys} section - The section of the card field to get the label for.
 * @returns {string} - The label of the card field section.
 */
export function getCardFieldLabel(shop: Shop, section: CardFieldKeys) {
  const cardContents = shop?.cardConfig?.cardContents ?? DEFAULT_CARD_CONTENTS;
  return cardContents[section];
}

export function getCardSectionIcon(
  cardConfig: Shop["cardConfig"],
  section: CardFieldKeys
): IconNames {
  const cardContents = cardConfig?.cardContents ?? DEFAULT_CARD_CONTENTS;

  const field = cardContents[section];
  switch (field) {
    case "Customer Last Name":
      return IconNames.user;
    case "Customer Name":
      return IconNames.user;
    case "Key Tag":
      return IconNames.tag;
    case "Year, Make, Model of vehicle":
      return IconNames.car;
    case "YY Model":
      return IconNames.car;
    case "RO Number":
      return IconNames.number;
    case "RO Number - Tag":
      return IconNames.number;
    case "Phone Number":
      return IconNames.phone;
    default:
      return IconNames.question;
  }
}

/**
 * Retrieves a specific section of a card based on the given parameters.
 * @param {Card | ActivityFeed} card - The card or activity feed object from which to extract the section.
 * @param {Shop} shop - The shop configuration containing card content settings.
 * @param {CardFieldKeys} section - The specific section key to retrieve from the card.
 * @param {CardFieldKey} [fieldOverride] - Resolve this field instead of the
 *   one configured for `section`. When omitted, behavior is byte-identical
 *   to calling without it. When provided, the manual-card hand-typed
 *   fallback (`card[section]`) only applies if the override names the same
 *   field the shop has actually configured for that slot — otherwise the
 *   hand-typed slot text isn't an answer to the field being asked for, so
 *   resolution falls through to the same detail-field path a non-manual
 *   card takes.
 * @returns {string | undefined} The content of the specified card section, or undefined if the section is not found.
 */
export function getCardSection(
  card: Card | ActivityFeed,
  shop: Shop,
  section: CardFieldKeys,
  fieldOverride?: CardFieldKey
) {
  const configuredField =
    !shop.cardConfig || !shop.cardConfig.cardContents
      ? "RO Number"
      : shop.cardConfig.cardContents[section];

  const field = fieldOverride ?? configuredField;

  // Helper function to check if detailed customer fields exist
  const hasDetailedCustomerFields = () => {
    return card.customer?.firstName || card.customer?.lastName;
  };

  // Helper function to check if detailed vehicle fields exist
  const hasDetailedVehicleFields = () => {
    return card.vehicle?.year || card.vehicle?.make || card.vehicle?.model;
  };

  // Helper function to check if detailed phone fields exist
  const hasDetailedPhoneFields = () => {
    return card.customer?.phone?.[0]?.number;
  };

  // For manual cards, use detailed fields if they exist, otherwise fall back
  // to card[section] — but only when we're resolving the field the shop
  // actually configured for this slot. A `fieldOverride` naming a different
  // field can't be answered by the hand-typed slot text.
  const manualFallbackApplies =
    card.source === "manual" &&
    card[section] !== "" &&
    (fieldOverride === undefined || fieldOverride === configuredField);

  if (manualFallbackApplies) {
    // Check if we should prefer detailed fields over card[section]
    switch (field) {
      case "Customer Last Name":
        if (card.customer?.lastName) {
          return cleanseUndefined(card.customer.lastName);
        }
        break;
      case "Customer Name":
        if (hasDetailedCustomerFields()) {
          return `${cleanseUndefined(card.customer?.firstName)} ${cleanseUndefined(card.customer?.lastName)}`;
        }
        break;
      case "Year, Make, Model of vehicle":
        if (hasDetailedVehicleFields()) {
          return `${cleanseUndefined(card.vehicle?.year)} ${cleanseUndefined(card.vehicle?.make)} ${cleanseUndefined(card.vehicle?.model)}`;
        }
        break;
      case "YY Model":
        if (hasDetailedVehicleFields()) {
          return `${cleanseUndefined(card.vehicle?.year).slice(-2)} ${cleanseUndefined(card.vehicle?.model)}`;
        }
        break;
      case "Phone Number":
        if (hasDetailedPhoneFields()) {
          return formatPhoneNumber(card.customer?.phone?.[0]?.number ?? "");
        }
        break;
    }

    // If no detailed fields exist for this field type, use card[section]
    return card[section];
  }

  // For non-manual cards or when card[section] is empty, use the detailed fields
  switch (field) {
    case "Customer Last Name":
      return cleanseUndefined(card.customer?.lastName);
    case "Key Tag":
      return cleanseUndefined(card.keyTag);
    case "Customer Name":
      return `${cleanseUndefined(card.customer?.firstName)} ${cleanseUndefined(card.customer?.lastName)}`;
    case "Year, Make, Model of vehicle":
      return `${cleanseUndefined(card.vehicle?.year)} ${cleanseUndefined(card.vehicle?.make)} ${cleanseUndefined(card.vehicle?.model)}`;
    case "YY Model":
      return `${cleanseUndefined(card.vehicle?.year).slice(-2)} ${cleanseUndefined(card.vehicle?.model)}`;
    case "RO Number":
      return `${cleanseUndefined(card.cardNumber)}`;
    case "RO Number - Tag":
      return card.keyTag
        ? `${cleanseUndefined(card.cardNumber)} - ${cleanseUndefined(card.keyTag)}`
        : `${cleanseUndefined(card.cardNumber)}`;
    case "Phone Number":
      return formatPhoneNumber(card.customer?.phone?.[0]?.number ?? "");
    default:
      return;
  }
}

/**
 * Generates a card details URL for the given shop and card IDs.
 * @param {string} shopId - The unique identifier for the shop.
 * @param {string} cardId - The unique identifier for the card.
 * @returns {string} The URL path to the card details page.
 */
export function generateCardDetailsUrl(shopId: string, cardId: string): string {
  return `/${shopId}/cards/${cardId}`;
}

/**
 * Checks if a card ID is valid (non-empty string).
 * @param {string} cardId - The card ID to validate.
 * @returns {boolean} True if the card ID is valid, false otherwise.
 */
export function isValidCardId(cardId: string): boolean {
  return typeof cardId === "string" && cardId.trim().length > 0;
}

/**
 * Generates the external integration URL for a card's repair order in the
 * shop's connected integration (Tekmetric or Shopware), mirroring the logic
 * `Card.Header.tsx`'s `LinkedLabel` builds inline. Takes the whole `shop` so
 * callers can't accidentally pass `shop.id` (the Firestore doc id) where
 * Tekmetric's own numeric `shop.shopId` is required.
 * @param {Integration} integration - The shop's integration configuration.
 * @param {Shop} shop - The shop the card belongs to.
 * @param {string} cardId - The unique identifier for the card.
 * @returns {string} The external integration URL, or an empty string if unsupported.
 */
export const generateIntegrationLinkUrl = (
  integration: Integration,
  shop: Shop,
  cardId: string
): string => {
  if (integration.integration === "tekmetric") {
    return `${integration.linkUrl}/admin/shop/${shop.shopId}/repair-orders/${cardId}/estimate`;
  }
  if (integration.integration === "shopware" && shop.integrationSubdomain) {
    return `${createSubdomainUrl(shop.integrationSubdomain, integration.linkUrl)}/work_orders/${cardId}`;
  }
  return "";
};

/**
 * Safely retrieves card contents from shop configuration with fallback to defaults.
 * @param {Shop} shop - The shop object containing card configuration.
 * @returns {CardContents} The card contents with defaults applied for undefined values.
 */
export function getCardContents(shop: Shop): CardContents {
  return { ...DEFAULT_CARD_CONTENTS, ...shop.cardConfig?.cardContents };
}

/**
 * Resolves the default color to preselect when a user opens the Force
 * Flash/Wiggle color picker. Shops always configure `cardColors` as
 * title-relabeled copies of the same fixed palette `DEFAULT_COLORS` derives
 * from (shop settings only allows renaming `title`/`description` — see
 * `ColorConfigurationSettings.tsx` — never `id` or the hex), so matching on
 * the Gold slot's stable `id` (rather than its shop-editable display title,
 * currently "Needs Attention" — see WEBV3-310) survives any past or future
 * rename and always resolves to the intended attention-signaling hex. This is
 * a deliberate change from matching-by-title: a shop that had renamed some
 * *other* slot to "Attention" is now redirected to Gold's hex regardless,
 * since the Force Effect preselect is meant to track the Gold slot itself,
 * not whatever an admin happened to label "Attention".
 * @param {Shop} shop - The shop object containing card color configuration.
 * @returns {string} The hex color to preselect.
 */
export function getDefaultForceEffectColor(shop: Shop): string {
  const cardColors = shop.cardConfig?.cardColors ?? DEFAULT_COLORS;
  const goldId = DEFAULT_COLORS.find((color) => color.title === "Gold")?.id;
  const attentionColor = cardColors.find((color) => color.id === goldId);
  return (attentionColor ?? cardColors[0] ?? DEFAULT_COLORS[0]).color;
}

/**
 * Builds the prefill values for a new manual card created via the
 * "Duplicate Card" 3-dot menu action (WEBV3-300). A one-time snapshot of
 * the source card, never kept in sync — see this plan's Global Constraints
 * for the exact field-by-field scope (what's copied and, just as
 * deliberately, what's excluded: tags, jobs, keyTag, label, and the legacy
 * custom field overrides).
 * @param {Card} sourceCard - The card being duplicated.
 * @returns {CreateCardDto} Prefill values for CardForm's `initialValues` prop.
 */
export function buildDuplicateCardPrefill(sourceCard: Card): CreateCardDto {
  const sourceCustomer = sourceCard.customer;
  const sourceVehicle = sourceCard.vehicle;

  const customer =
    sourceCustomer && "schemaVersion" in sourceCustomer
      ? ({
          schemaVersion: 2 as const,
          firstName: sourceCustomer.firstName,
          lastName: sourceCustomer.lastName,
          contactFirstName: sourceCustomer.contactFirstName,
          contactLastName: sourceCustomer.contactLastName,
          name: sourceCustomer.name,
          phone: sourceCustomer.phone,
        } as CreateCardDto["customer"])
      : undefined;

  const vehicle =
    sourceVehicle && "schemaVersion" in sourceVehicle
      ? ({
          schemaVersion: 2 as const,
          year: sourceVehicle.year,
          make: sourceVehicle.make,
          model: sourceVehicle.model,
        } as CreateCardDto["vehicle"])
      : undefined;

  return {
    source: "manual",
    cardNumber: sourceCard.cardNumber ? `${sourceCard.cardNumber}-D` : "",
    customer,
    vehicle,
    ...(sourceCard.technicianId !== undefined && {
      technicianId: sourceCard.technicianId,
    }),
    technicianIds: sourceCard.technicianIds,
    ...(sourceCard.serviceWriterId !== undefined && {
      serviceWriterId: sourceCard.serviceWriterId,
    }),
    // Boolean, not an object/reference type -- default to `false` rather
    // than omitting, matching CardForm's own `defaultValues.isWaiter`.
    isWaiter: sourceCard.isWaiter ?? false,
    dropOffTime: sourceCard.dropOffTime,
    promisedTime: sourceCard.promisedTime,
    appointment: sourceCard.appointment,
    laborSales: sourceCard.laborSales,
    partsSales: sourceCard.partsSales,
    subletSales: sourceCard.subletSales,
    ...(sourceCard.totalLaborHours !== undefined && {
      totalLaborHours: sourceCard.totalLaborHours,
    }),
    completedDate: sourceCard.completedDate,
    duplicatedFrom: {
      cardId: sourceCard.id,
      cardNumber: sourceCard.cardNumber ?? null,
    },
  };
}
